import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您访问的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const cat = categories.find((c) => c.id === article.category);
  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: '男同网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://nantong.wang/article/${article.slug}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <article>
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: cat?.name || '', path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="relative w-full h-64 md:h-96 rounded-lg overflow-hidden mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={`${article.title} - 男同网`}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Link
                to={`/category/${article.category}`}
                className="text-xs font-medium px-3 py-1 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                {cat?.name}
              </Link>
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 font-heading mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-400 mb-8 pb-8 border-b border-background-200/70">
              <span>{article.author}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.publishDate}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.readTime}</span>
            </div>

            <div
              className="prose prose-base md:prose-lg max-w-none text-foreground-700 leading-relaxed
                prose-headings:text-foreground-900 prose-headings:font-heading prose-headings:font-bold
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                prose-p:leading-relaxed prose-p:mb-4
                prose-ul:list-disc prose-ul:pl-5
                prose-li:mb-2
                prose-strong:text-foreground-900"
            >
              <div dangerouslySetInnerHTML={{ __html: article.content }} />
            </div>

            <div className="mt-10 pt-6 border-t border-background-200/70 flex flex-wrap items-center gap-3">
              <span className="text-sm text-foreground-500">分享文章：</span>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                <i className="ri-link"></i>
              </button>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100 mt-10">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => {
                  const raCat = categories.find((c) => c.id === ra.category);
                  return (
                    <ArticleCard
                      key={ra.id}
                      slug={ra.slug}
                      title={ra.title}
                      excerpt={ra.excerpt}
                      category={ra.category}
                      categoryName={raCat?.name || ''}
                      author={ra.author}
                      publishDate={ra.publishDate}
                      readTime={ra.readTime}
                      imageUrl={ra.imageUrl}
                    />
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}